import {
    defaultSetOption,
    findSetOption,
    fromCents,
    setDiscountCents,
    setPositionFilterName,
    setPositionToFilter,
    toCents,
    type SetPositionColumn,
} from '@/lib/products/set';
import type {
    ConfigurableProductFilterObject,
    ConfigurableProductFilterOptionObject,
    ConfigurableProductSetDiscountObject,
    ConfigurableProductSetObject,
    ConfigurableProductSetOptionObject,
    ConfigurableProductSetPositionObject,
} from '@/types/generated';
import _ from 'lodash';
import { computed, ref, type ComputedRef } from 'vue';

/**
 * Tracks the chosen size per set position and calculates the set's subtotal, discount and total.
 *
 * A meerluik (`isPanelSet`) has nothing to choose: its panels are fixed and it is sold as one
 * product at the master price, so the totals come off the master rather than from selections.
 */
export const useProductSetComposable = (
    set: ConfigurableProductSetObject | null | undefined,
    positionLabel: (number: number) => string,
    isPanelSet = false,
) => {
    const positionState = buildPositionState(set, positionLabel);
    const selectionState = buildSelectionState(positionState);
    const pricing = buildPricing(set, isPanelSet, positionState.positions, selectionState.selectedOptions);

    selectionState.resetToDefaults();

    return {
        isPanelSet,
        ...positionState,
        ...selectionState,
        ...pricing,
    };
};

type PositionState = ReturnType<typeof buildPositionState>;

function buildPositionState(set: ConfigurableProductSetObject | null | undefined, positionLabel: (number: number) => string) {
    const positions = computed<ConfigurableProductSetPositionObject[]>(() => _.sortBy(set?.positions ?? [], 'position'));

    /** Only positions with options get a dropdown, since the menu needs a selected option. */
    const positionFilters = computed<ConfigurableProductFilterObject[]>(() =>
        positions.value
            .map((position: ConfigurableProductSetPositionObject, index: number) =>
                position.options.length > 0 ? setPositionToFilter(position, index, positionLabel(index + 1)) : undefined,
            )
            .filter((filter): filter is ConfigurableProductFilterObject => filter !== undefined),
    );

    /** Each position that has a dropdown, paired with its image. */
    const positionColumns = computed<SetPositionColumn[]>(() =>
        positions.value
            .map((position: ConfigurableProductSetPositionObject, index: number) => {
                const name = setPositionFilterName(index);
                const filter = positionFilters.value.find((candidate: ConfigurableProductFilterObject) => candidate.meta.name === name);

                return filter ? { filter, image: position.image } : undefined;
            })
            .filter((column): column is SetPositionColumn => column !== undefined),
    );

    return { positions, positionFilters, positionColumns };
}

function buildSelectionState({ positions, positionFilters }: PositionState) {
    const selectedFilterOptions = ref<Record<string, ConfigurableProductFilterOptionObject>>({});
    const confirmedPositions = ref<Record<string, boolean>>({});

    /** Chosen option per position, index for index with `positions`. An optionless position leaves a hole. */
    const selectedOptions = computed<(ConfigurableProductSetOptionObject | undefined)[]>(() =>
        positions.value.map((position: ConfigurableProductSetPositionObject, index: number) => {
            const chosenSku = selectedFilterOptions.value[setPositionFilterName(index)]?.value;

            return findSetOption(position, chosenSku) ?? defaultSetOption(position);
        }),
    );

    /** Every position offering a real choice is confirmed; single-option ones need no confirming. */
    const hasUserSelected = computed<boolean>(() =>
        positionFilters.value
            .filter((filter: ConfigurableProductFilterObject) => filter.options.length > 1)
            .every((filter: ConfigurableProductFilterObject) => confirmedPositions.value[filter.meta.name] === true),
    );

    function selectOption(filterName: string, option: ConfigurableProductFilterOptionObject): void {
        selectedFilterOptions.value[filterName] = option;
        confirmedPositions.value[filterName] = true;
    }

    function resetToDefaults(): void {
        confirmedPositions.value = {};
        selectedFilterOptions.value = defaultFilterSelections(positions.value, positionFilters.value);
    }

    return { selectedFilterOptions, selectedOptions, hasUserSelected, selectOption, resetToDefaults };
}

/** The default option of every position, keyed by its filter name, as the initial dropdown state. */
function defaultFilterSelections(
    positions: ConfigurableProductSetPositionObject[],
    positionFilters: ConfigurableProductFilterObject[],
): Record<string, ConfigurableProductFilterOptionObject> {
    return Object.fromEntries(
        positions.flatMap((position: ConfigurableProductSetPositionObject, index: number) => {
            const option = defaultSetOption(position);

            if (option === undefined) {
                return [];
            }

            const name = setPositionFilterName(index);
            // By name, not by index: positionFilters skips optionless positions.
            const filter = positionFilters.find((candidate: ConfigurableProductFilterObject) => candidate.meta.name === name);
            const filterOption = filter?.options.find((candidate: ConfigurableProductFilterOptionObject) => candidate.value === option.sku);

            return filterOption ? [[name, filterOption]] : [];
        }),
    );
}

function buildPricing(
    set: ConfigurableProductSetObject | null | undefined,
    isPanelSet: boolean,
    positions: ComputedRef<ConfigurableProductSetPositionObject[]>,
    selectedOptions: ComputedRef<(ConfigurableProductSetOptionObject | undefined)[]>,
) {
    /** The chosen options with the empty positions removed, for summing prices. */
    const resolvedOptions = computed<ConfigurableProductSetOptionObject[]>(() =>
        selectedOptions.value.filter((option): option is ConfigurableProductSetOptionObject => option !== undefined),
    );

    const masterCents = computed<number>(() => toCents(set?.master?.price ?? 0));
    const subtotalCents = computed<number>(() =>
        isPanelSet
            ? panelSubtotalCents(masterCents.value, set?.discount)
            : _.sumBy(resolvedOptions.value, (option: ConfigurableProductSetOptionObject) => toCents(option.price)),
    );
    const discountCents = computed<number>(() =>
        isPanelSet ? subtotalCents.value - masterCents.value : setDiscountCents(subtotalCents.value, set?.discount),
    );
    const totalCents = computed<number>(() => (isPanelSet ? masterCents.value : Math.max(subtotalCents.value - discountCents.value, 0)));

    /**
     * A position with no option cannot be ordered, so the set cannot be. A total of zero still
     * can. A meerluik has no selections to satisfy — its master price is what makes it orderable.
     */
    const isBuyable = computed<boolean>(() =>
        isPanelSet ? masterCents.value > 0 : positions.value.length > 0 && !selectedOptions.value.includes(undefined),
    );

    return {
        subtotal: computed<number>(() => fromCents(subtotalCents.value)),
        discountAmount: computed<number>(() => fromCents(discountCents.value)),
        total: computed<number>(() => fromCents(totalCents.value)),
        hasDiscount: computed<boolean>(() => discountCents.value > 0),
        discountPercentage: computed<number | null>(() => (set?.discount?.is_percentage === true ? (set?.discount?.amount ?? null) : null)),
        isBuyable,
    };
}

/**
 * A meerluik's pre-discount price, reconstructed from the sold (master) price: the payload
 * carries no per-panel prices to sum. Exact for an absolute discount; for a percentage it is
 * the cents-rounded inverse, which is display-only.
 */
function panelSubtotalCents(masterCents: number, discount: ConfigurableProductSetDiscountObject | undefined): number {
    if (!discount || discount.amount <= 0) {
        return masterCents;
    }

    if (!discount.is_percentage) {
        return masterCents + toCents(discount.amount);
    }

    if (discount.amount >= 100) {
        return masterCents;
    }

    return Math.round((masterCents * 100) / (100 - discount.amount));
}
